import { IProjectSchema } from '@/api/project';
import { Button, Dialog, Modal, ModalRef } from 'alurkerja-ui';
import React, { useRef } from 'react';
import { FaUserPlus } from 'react-icons/fa';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { useForm } from 'react-hook-form';
import { SelectField } from '@/components/form/fields/SelectField';
import { Loading } from '@/components/Loading';
import { zodResolver } from '@hookform/resolvers/zod';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import {
  IProjectAssignSchema,
  ProjectAssigneeService,
  projectAssignSchema,
} from '@/api/projectAssignee';
import { accessOptions } from '@/utils/constants/buttonAssign';
import { FaPen } from 'react-icons/fa6';
import { SelectMultipleField } from '@/components/form/fields/SelectMultipleField';

type ButtonAssignProps = {
  assigneeId: string | null;
  data: IProjectSchema;
  onSuccess: () => void;
};

interface ModalContentProps extends ButtonAssignProps {
  closeModal?: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  closeModal,
  assigneeId,
  data: projectData,
  onSuccess,
}) => {
  const { data, isFetching: isInitialLoading } =
    ProjectAssigneeService.useGetContributorsAndRoles(projectData.id!);
  const { mutate: create, isPending: isLoading } =
    ProjectAssigneeService.useCreate();
  const { mutate: update, isPending: isUpdating } =
    ProjectAssigneeService.useUpdate();
  const { data: asigneeData } = ProjectAssigneeService.useGet(assigneeId!);

  const form = useForm<IProjectAssignSchema>({
    resolver: zodResolver(projectAssignSchema),
    values: asigneeData,
  });

  const submitAction = (data: IProjectAssignSchema) => {
    data.project_id = projectData.id;

    if (assigneeId) {
      update(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'Assignee has been updated',
            callback: () => {
              closeModal?.();

              onSuccess();
            },
          });
        },
      });
    } else {
      create(data, {
        onSuccess: () => {
          Dialog.success({
            title: 'Success',
            description: 'User has been assigned',
            callback: () => {
              closeModal?.();

              onSuccess();
            },
          });
        },
        onError: () => {
          Dialog.error({
            title: 'Failed',
            description:
              'An error occurred when assigning this User or this User has already been assigned to this project.',
          });
        },
      });
    }
  };

  return (
    <FormContainer form={form} onSubmit={submitAction} className="p-4">
      {isInitialLoading ? (
        <Loading />
      ) : (
        <div>
          <SelectMultipleField
            name="contributor_id"
            label="Contributor"
            options={data?.contributor.map(e => ({
              value: e.id!,
              label: e.first_name + ' ' + e.last_name,
            }))}
            hasRequiredAsterisk
            isSearchable
            isDynamicHeight
          />

          <SelectField
            name="access"
            label="Access"
            options={accessOptions}
            hasRequiredAsterisk
          />

          <FormFooter>
            <OutlineButton onClick={closeModal}>Cancel</OutlineButton>

            <Button loading={isLoading || isUpdating}>Save</Button>
          </FormFooter>
        </div>
      )}
    </FormContainer>
  );
};

export const ButtonAssign: React.FC<ButtonAssignProps> = ({
  assigneeId,
  data,
  onSuccess,
}) => {
  const ref = useRef<ModalRef>(null);

  const buttonIcon = () => {
    if (assigneeId) {
      return (
        <Button className="bg-orange-400">
          <FaPen size={15} />
        </Button>
      );
    } else {
      return (
        <Button className="bg-cyan-500">
          <FaUserPlus size={17} className="text-green-100" />
        </Button>
      );
    }
  };

  return (
    <Modal
      ref={ref}
      title={`${data.title} - Assign User`}
      triggerButton={buttonIcon()}
    >
      <ModalContent
        assigneeId={assigneeId}
        closeModal={ref.current?.closeModal}
        data={data}
        onSuccess={onSuccess}
      />
    </Modal>
  );
};
